import { useState, useRef, useCallback, useEffect } from 'react'

export type RecordingState = 'idle' | 'recording' | 'stopped'

const MIME_TYPE_PREFERENCE = [
  'audio/mp4;codecs=mp4a.40.2',
  'audio/mp4',
  'audio/aac',
  'audio/webm;codecs=opus',
  'audio/webm',
]

interface UseAudioRecorderReturn {
  recordingState: RecordingState
  recordingTime: number
  audioBlob: Blob | null
  audioUrl: string | null
  startRecording: () => Promise<void>
  stopRecording: (onComplete?: (blob: Blob, duration: number) => void) => void
  cancelRecording: () => void
  error: string | null
}

export const useAudioRecorder = (): UseAudioRecorderReturn => {
  const [recordingState, setRecordingState] = useState<RecordingState>('idle')
  const [recordingTime, setRecordingTime] = useState(0)
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null)
  const [audioUrl, setAudioUrl] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const timerRef = useRef<NodeJS.Timeout | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const onStopCallbackRef = useRef<((blob: Blob, duration: number) => void) | null>(null)
  const recordingTimeRef = useRef<number>(0)
  const recordingMimeTypeRef = useRef<string>('')

  // Keep recordingTimeRef in sync with recordingTime
  useEffect(() => {
    recordingTimeRef.current = recordingTime
  }, [recordingTime])

  // Cleanup blob URL to prevent memory leaks
  useEffect(() => {
    return () => {
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl)
      }
    }
  }, [audioUrl])

  // Timer effect - only runs when recording
  useEffect(() => {
    if (recordingState === 'recording') {
      // Clear any existing timer first
      if (timerRef.current) {
        clearInterval(timerRef.current)
      }

      // Start new timer
      timerRef.current = setInterval(() => {
        setRecordingTime((prev) => prev + 1)
      }, 1000)

      // Cleanup on unmount or when recording stops
      return () => {
        if (timerRef.current) {
          clearInterval(timerRef.current)
          timerRef.current = null
        }
      }
    } else {
      // Stop timer when not recording
      if (timerRef.current) {
        clearInterval(timerRef.current)
        timerRef.current = null
      }
    }
  }, [recordingState])

  const startRecording = useCallback(async () => {
    if (typeof MediaRecorder === 'undefined') {
      setError('Audio recording is not supported in this browser')
      setRecordingState('idle')
      return
    }

    try {
      setError(null)
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      streamRef.current = stream

      const selectedMimeType = MIME_TYPE_PREFERENCE.find(type => MediaRecorder.isTypeSupported(type)) || ''

      recordingMimeTypeRef.current = selectedMimeType

      const mediaRecorder = new MediaRecorder(
        stream,
        selectedMimeType ? { mimeType: selectedMimeType } : undefined,
      )

      if (!recordingMimeTypeRef.current) {
        recordingMimeTypeRef.current = mediaRecorder.mimeType
      }

      mediaRecorderRef.current = mediaRecorder
      chunksRef.current = []

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data)
        }
      }

      mediaRecorder.onstop = () => {
        const mimeType = recordingMimeTypeRef.current || mediaRecorderRef.current?.mimeType || 'audio/webm'
        const blob = new Blob(chunksRef.current, { type: mimeType })
        const url = URL.createObjectURL(blob)
        setAudioBlob(blob)
        setAudioUrl(url)
        setRecordingState('stopped')

        // Call the callback if it exists
        if (onStopCallbackRef.current) {
          const duration = recordingTimeRef.current
          onStopCallbackRef.current(blob, duration)
          onStopCallbackRef.current = null
        }

        // Clean up stream
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop())
          streamRef.current = null
        }
      }

      mediaRecorder.start()
      setRecordingTime(0)
      setRecordingState('recording')
    } catch (err) {
      console.error('Error starting recording:', err)
      setError(err instanceof Error ? err.message : 'Failed to start recording')
      setRecordingState('idle')
    }
  }, [])

  const stopRecording = useCallback((onComplete?: (blob: Blob, duration: number) => void) => {
    if (mediaRecorderRef.current && recordingState === 'recording') {
      // Save the callback to be called when onstop is triggered
      if (onComplete) {
        onStopCallbackRef.current = onComplete
      }
      mediaRecorderRef.current.stop()
    }
  }, [recordingState])

  const cancelRecording = useCallback(() => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop()
      chunksRef.current = []
      setAudioBlob(null)
      setAudioUrl(null)
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop())
      streamRef.current = null
    }

    setRecordingTime(0)
    setRecordingState('idle')
    setError(null)
  }, [])

  return {
    recordingState,
    recordingTime,
    audioBlob,
    audioUrl,
    startRecording,
    stopRecording,
    cancelRecording,
    error,
  }
}
